import React from "react";
import type { DebounceSettings } from "./useDebouncedCallback";
import useDebouncedCallback from "./useDebouncedCallback";

interface Settings {
  fireOnInit: boolean;
  debounced: number | boolean;
  debounceSettings?: DebounceSettings;
}

const useObserverCallback = <T extends Element>(
  callback: (entry: ResizeObserverEntry & { target: T }) => void,
  deps: unknown[],
  target: T | React.RefObject<T>,
  { fireOnInit, debounced, debounceSettings }: Settings,
) => {
  const firstRun = React.useRef(true);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedCallback = useDebouncedCallback(callback, [deps], debounced, debounceSettings);

  React.useLayoutEffect(() => {
    const resizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]) => {
      const entry = entries[0];
      if (entries.length > 1) throw new Error("Multi-target not supported?");
      if (!entry) throw new Error("No entry found?");
      const castedEntry = entry as ResizeObserverEntry & { target: T };
      if (firstRun.current) {
        firstRun.current = false;
        if (fireOnInit) callback(castedEntry);
      } else debouncedCallback(castedEntry);
    });

    const element = "current" in target ? target.current : target;
    if (!element) return;
    resizeObserver.observe(element);

    return () => {
      resizeObserver.unobserve(element);
      resizeObserver.disconnect();
    };
  }, [callback, debounced, debouncedCallback, fireOnInit, target]);
};

export default useObserverCallback;
